// fetch wrapper for backoffice → Hono Lambda.
//
// All auth + content endpoints now live in the Hono API at api.drivecars.ai.
// Payload's REST surface is for the /admin UI only.
//
// Behaviours:
//   - Reads the JWT from the auth store (localStorage-backed) and adds
//     `Authorization: JWT <token>` on every call.
//   - On 401, clears the stored token and throws `UnauthorizedError` for
//     the caller to redirect to /login. (Hono issues long-lived JWTs;
//     no refresh-token endpoint.)
//   - JSON-encodes a body when present and sets the Content-Type header.
//   - Throws `ApiError` with status + parsed body on non-2xx responses.

import { env } from './env';
import { authStore } from './auth-store';

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public body?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export class UnauthorizedError extends ApiError {
  constructor(message = 'Unauthorized') {
    super(401, message);
    this.name = 'UnauthorizedError';
  }
}

interface ApiOptions {
  /** Absolute URL or path relative to the Hono API base. */
  path: string;
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  /** Override base URL — defaults to env.API_BASE_URL. Use to point at Payload. */
  baseUrl?: string;
  /** Don't try to refresh on 401 (e.g. when the call IS the refresh). */
  skipRefresh?: boolean;
  /** Additional headers. */
  headers?: Record<string, string>;
  /** AbortSignal pass-through. */
  signal?: AbortSignal;
}

async function rawFetch(opts: ApiOptions): Promise<Response> {
  const base = (opts.baseUrl ?? env().API_BASE_URL).replace(/\/$/, '');
  const url = opts.path.startsWith('http')
    ? opts.path
    : `${base}${opts.path.startsWith('/') ? opts.path : `/${opts.path}`}`;
  const headers: Record<string, string> = {
    Accept: 'application/json',
    ...opts.headers,
  };
  const token = authStore.getToken();
  if (token) headers.Authorization = `JWT ${token}`;
  const init: RequestInit = {
    method: opts.method ?? 'GET',
    headers,
    signal: opts.signal,
  };
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(opts.body);
  }
  return fetch(url, init);
}

export async function api<T>(opts: ApiOptions): Promise<T> {
  const res = await rawFetch(opts);
  if (res.status === 401 && !opts.skipRefresh) {
    authStore.clear();
    throw new UnauthorizedError();
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) ?? (await res.text().catch(() => null));
    throw new ApiError(res.status, `${res.status} ${res.statusText}`, body);
  }
  // 204 No Content carries no JSON body.
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

/**
 * Same auth story as `api()` but yields the raw Response so the caller can
 * read it as a Blob/stream. Use this for CSV exports, file downloads, etc.
 * — anywhere the response isn't JSON.
 */
export async function apiRaw(opts: ApiOptions): Promise<Response> {
  const res = await rawFetch(opts);
  if (res.status === 401 && !opts.skipRefresh) {
    authStore.clear();
    throw new UnauthorizedError();
  }
  if (!res.ok) {
    const body = (await res.json().catch(() => null)) ?? (await res.text().catch(() => null));
    throw new ApiError(res.status, `${res.status} ${res.statusText}`, body);
  }
  return res;
}

// ─── Auth helpers — call the Hono API ────────────────────────────────

export interface PayloadLoginResponse {
  token: string;
  exp: number;
  user: {
    id: number;
    email: string;
    role?: string;
    /** Backoffice scoped role (added with 0093 migration). NULL/undefined
     *  → unassigned. Login response doesn't populate this; /auth/me does. */
    staffRole?:
      | 'super_admin' | 'operations' | 'finance' | 'marketing' | 'support' | 'viewer'
      | null;
    firstName?: string;
    lastName?: string;
  };
}

// Function names preserved for callsite stability; they now hit Hono
// (api.drivecars.ai/auth/*), not Payload (cms.drivecars.ai/api/users/*).
// Response shapes intentionally match what Payload returned.
//
// Backoffice = ops/admin surface — every authenticated call lands on
// /admin/* routes that require an admin JWT. The audience login routes
// (/auth/vendor/*, /auth/customer/*) query app.vendors / app.customers
// respectively and never accept admin emails, so we must hit /auth/login
// (which queries app.users) here.

export async function payloadLogin(email: string, password: string): Promise<PayloadLoginResponse> {
  return api<PayloadLoginResponse>({
    path: '/auth/login',
    method: 'POST',
    body: { email, password },
    skipRefresh: true,
  });
}

export async function payloadMe(): Promise<{ user: PayloadLoginResponse['user'] | null }> {
  // Hono's /auth/me returns 401 when unauthenticated. Trap into { user: null }
  // so callers see the same shape as the old Payload variant.
  try {
    return await api({ path: '/auth/me', skipRefresh: true });
  } catch (err) {
    if (err instanceof UnauthorizedError) return { user: null };
    throw err;
  }
}

export async function payloadLogout(): Promise<void> {
  // Hono JWTs are stateless — no server-side logout endpoint. Just
  // discard the local token; subsequent requests will 401.
  authStore.clear();
}

// ─── Password reset ──────────────────────────────────────────────────

/**
 * Request a reset link. Always resolves to 200 — Hono never discloses
 * whether the email is registered. Caller renders the same neutral UX
 * regardless.
 */
export async function forgotPassword(email: string): Promise<void> {
  await api({
    path: '/auth/forgot-password',
    method: 'POST',
    body: { email },
    skipRefresh: true,
  });
}

/**
 * Submit a new password using the token from the reset email. On success
 * Hono returns the same `{ token, exp, user }` shape as /auth/login so
 * the caller can drop the operator straight into /dashboard.
 */
export async function resetPassword(
  token: string,
  password: string,
): Promise<PayloadLoginResponse> {
  return api<PayloadLoginResponse>({
    path: '/auth/reset-password',
    method: 'POST',
    body: { token, password },
    skipRefresh: true,
  });
}
